import { Image } from '@components/products/products'
import { API_URL } from '@constants'
import axiosReadInstance from '../../request/axiosReadInstance'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/solid'
import Carousel from 'react-simply-carousel'
import { replaceImageExtension } from '@utils'
import { usePageLoading } from '../../context/PageLoadingContext'
import { CarouselSlideTablist } from '@components/carousel/CarouselSlideTablist'
import { formatCarouselSlideAnnouncement } from '@components/carousel/carouselLiveRegion'
import {
    carouselContainerProps,
    carouselInnerProps,
    getCarouselShellClassName,
    getFullWidthSlideStyle,
    useCarouselMount,
} from '@components/carousel/useCarouselReady'

interface Feedback {
    name: string
    feedback: string
    job: string
    image: Image
    id: number
    company: number
}

interface IWhyJoin {
    title: string
    testimonialJoin: Array<Feedback>
}

function formatWhyJoinSlideLive(item: Feedback) {
    const companyText =
        item.company !== undefined && item.company !== null
            ? String(item.company)
            : ''
    return [item.name, item.job, companyText, item.feedback]
        .filter((p) => p !== '')
        .join(', ')
}

export const WhyJoin = () => {
    const [whyJoin, setWhyJoin] = useState<IWhyJoin>()
    const [visibleItems, setVisibleItems] = useState<Feedback[]>([])
    const [activeSlide, setActiveSlide] = useState(0)
    const [carouselLiveMessage, setCarouselLiveMessage] = useState('')
    const skipCarouselLiveRef = useRef(true)
    const [isLoading, setIsLoading] = useState(true)
    const { incrementLoadingRequestCount, decrementLoadingRequestCount } =
        usePageLoading()

    const carouselBtnClass =
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-full'

    const fetchData = useCallback(async () => {
        setIsLoading(true)
        incrementLoadingRequestCount()
        await axiosReadInstance
            .get(`${API_URL}/api/why-join?populate=deep`)
            .then((response) => {
                response.data.data.attributes.testimonialJoin =
                    response.data.data.attributes.testimonialJoin.map(
                        (testimonial: Feedback) => ({
                            company: testimonial.company,
                            job: testimonial.job,
                            image: testimonial.image,
                            feedback: testimonial.feedback,
                            id: testimonial.id,
                            name: testimonial.name,
                            isActive:
                                testimonial.id >= 1 && testimonial.id <= 3
                                    ? true
                                    : false,
                        })
                    )
                setWhyJoin(response.data.data.attributes)
                setVisibleItems(response.data.data.attributes.testimonialJoin)
            })
            .finally(() => {
                setIsLoading(false)
                decrementLoadingRequestCount()
            })
    }, [incrementLoadingRequestCount, decrementLoadingRequestCount])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    useEffect(() => {
        if (!visibleItems?.length) return
        const item = visibleItems[activeSlide]
        if (!item) return
        if (skipCarouselLiveRef.current) {
            skipCarouselLiveRef.current = false
            return
        }
        setCarouselLiveMessage(
            formatCarouselSlideAnnouncement({
                slideIndex: activeSlide,
                totalSlides: visibleItems.length,
                content: formatWhyJoinSlideLive(item),
            })
        )
    }, [activeSlide, visibleItems])

    const slideCount = visibleItems?.length ?? 0
    const itemsToScroll = 1
    const {
        shellRef,
        containerWidth,
        canMount,
        isLayoutStable,
        markLayoutStable,
    } = useCarouselMount(!isLoading && slideCount > 0)
    const slideStyle =
        containerWidth > 0 ? getFullWidthSlideStyle(containerWidth) : undefined

    const handleSlideChange = useCallback(
        (slideIndex: number) => {
            setActiveSlide(slideIndex)
            markLayoutStable()
        },
        [markLayoutStable]
    )

    return (
        <section
            className="bg-white mb-20"
            aria-labelledby="why-join-carousel-heading"
            aria-roledescription="carousel"
        >
            <div className="max-w-screen-2xl px-4  lg:py-16">
                <h2
                    id="why-join-carousel-heading"
                    className="mb-4 md:px-[8rem] text-lg font-semibold tracking-tight leading-none text-gray-900 md:text-5xl lg:text-1xl"
                >
                    {whyJoin?.title}
                </h2>
                {isLoading
                    ? null
                    : whyJoin &&
                      whyJoin?.testimonialJoin.length > 0 && (
                          <>
                              <p
                                  aria-live="polite"
                                  aria-atomic="true"
                                  className="sr-only"
                              >
                                  {carouselLiveMessage}
                              </p>
                              <div
                                  ref={shellRef}
                                  className={getCarouselShellClassName(
                                      isLayoutStable
                                  )}
                              >
                                  {canMount && slideStyle && (
                                      <Carousel
                                          containerProps={
                                              carouselContainerProps
                                          }
                                          innerProps={carouselInnerProps}
                                          infinite={true}
                                          centerMode={true}
                                          preventScrollOnSwipe
                                          swipeTreshold={60}
                                          activeSlideIndex={activeSlide}
                                          onRequestChange={handleSlideChange}
                                          activeSlideProps={{
                                              style: slideStyle,
                                          }}
                                          visibleSlideProps={{
                                              style: slideStyle,
                                          }}
                                          responsiveProps={[
                                              {
                                                  itemsToShow: 1,
                                                  itemsToScroll: 1,
                                                  minWidth: 768,
                                              },
                                          ]}
                                          forwardBtnProps={{
                                              'aria-label':
                                                  'Témoignages suivants',
                                              children: (
                                                  <ArrowRightIcon
                                                      aria-hidden={true}
                                                      className="border rounded-full p-2 w-12 h-12 md:w-16 md:h-16"
                                                      style={{
                                                          borderColor:
                                                              '#D6D6DC',
                                                      }}
                                                  />
                                              ),
                                              style: {
                                                  width: 60,
                                                  height: 60,
                                                  minWidth: 60,
                                                  alignSelf: 'center',
                                              },
                                              className: carouselBtnClass,
                                          }}
                                          backwardBtnProps={{
                                              'aria-label':
                                                  'Témoignages précédents',
                                              children: (
                                                  <ArrowLeftIcon
                                                      aria-hidden={true}
                                                      className="border rounded-full p-2 w-12 h-12 md:w-16 md:h-16"
                                                      style={{
                                                          borderColor:
                                                              '#D6D6DC',
                                                      }}
                                                  />
                                              ),
                                              style: {
                                                  width: 60,
                                                  height: 60,
                                                  minWidth: 60,
                                                  alignSelf: 'center',
                                              },
                                              className: carouselBtnClass,
                                          }}
                                          dotsNav={{ show: false }}
                                          itemsToShow={1}
                                          speed={400}
                                      >
                                          {visibleItems?.map(
                                              (testimonialDesire, index) => {
                                                  return (
                                                      <div
                                                          key={
                                                              testimonialDesire.id ??
                                                              index
                                                          }
                                                          id={`why-join-slide-${index}`}
                                                          aria-roledescription="slide"
                                                          aria-label={`Témoignage ${
                                                              index + 1
                                                          } sur ${slideCount}`}
                                                          className="grid grid-cols-1 lg:grid-cols-2 object-cover text-justify h-auto ml-2 mr-2 md:pr-12 snap-start pb-8 md:pb-2 box-border"
                                                          style={slideStyle}
                                                      >
                                                          <div className="self-center justify-self-center px-16 pt-16 pb-0 lg:p-16">
                                                              <img
                                                                  className="h-auto w-full max-w-full object-contain"
                                                                  style={{
                                                                      borderColor:
                                                                          '#fff',
                                                                  }}
                                                                  src={replaceImageExtension(
                                                                      testimonialDesire
                                                                          .image
                                                                          .data
                                                                          .attributes
                                                                          .url
                                                                  )}
                                                                  alt={
                                                                      testimonialDesire.name
                                                                          ? ''
                                                                          : testimonialDesire
                                                                                .image
                                                                                .data
                                                                                .attributes
                                                                                .alternativeText ||
                                                                            ''
                                                                  }
                                                                  aria-hidden={
                                                                      testimonialDesire.name
                                                                          ? true
                                                                          : undefined
                                                                  }
                                                              />
                                                          </div>
                                                          <div className="self-center flex flex-col gap-4">
                                                              {testimonialDesire.job && (
                                                                  <p className="text-sm md:text-base text-gray-700">
                                                                      {
                                                                          testimonialDesire.job
                                                                      }
                                                                  </p>
                                                              )}
                                                              <q className="text-sm w-full md:text-base self-center text-justify font-primary tracking-tight text-gray-900 min-h-[65px]">
                                                                  {
                                                                      testimonialDesire.feedback
                                                                  }
                                                              </q>
                                                              <p className="text-sm md:text-3xl font-semibold text-left self-center mt-4">
                                                                  {
                                                                      testimonialDesire.name
                                                                  }
                                                                  {testimonialDesire.company !==
                                                                      undefined &&
                                                                  testimonialDesire.company !==
                                                                      null
                                                                      ? `, ${testimonialDesire.company}`
                                                                      : ''}
                                                              </p>
                                                          </div>
                                                      </div>
                                                  )
                                              }
                                          )}
                                      </Carousel>
                                  )}
                              </div>
                              <CarouselSlideTablist
                                  tabCount={slideCount}
                                  activeSlideIndex={activeSlide}
                                  itemsToScroll={itemsToScroll}
                                  totalSlides={slideCount}
                                  onRequestChange={setActiveSlide}
                                  tablistLabel="Témoignages"
                                  getPanelIdForSlide={(i) =>
                                      `why-join-slide-${i}`
                                  }
                              />
                          </>
                      )}
            </div>
        </section>
    )
}
